/**
 * Accounts: Firebase Auth (email + password), the same users the Unity app
 * creates at register/login (FirebaseManager.cs). Accounts created here live
 * only in Firebase (no legacy PHP account): everything the web does stays in
 * Firebase, and the Unity app will read it from there later. Signing in with
 * Apple (the accounts the iOS app creates with Apple) is offered at sign-in
 * only, not when creating an account.
 */
import type { User } from "firebase/auth";
import { getApp, FUNCTIONS_REGION } from "../firebase";

export type AuthUser = Pick<User, "uid" | "email" | "displayName"> & {
  /** How the account signs in (an account made with Apple on iOS has no password). */
  hasPassword: boolean;
  hasApple: boolean;
};

let authPromise: Promise<typeof import("firebase/auth")> | null = null;
const authModule = () => (authPromise ??= import("firebase/auth"));

const listeners = new Set<(user: AuthUser | null) => void>();
const plain = (u: User | null): AuthUser | null =>
  u
    ? {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName,
        hasPassword: u.providerData.some((p) => p.providerId === "password"),
        hasApple: u.providerData.some((p) => p.providerId === "apple.com"),
      }
    : null;

export async function watchAuth(cb: (user: AuthUser | null) => void): Promise<() => void> {
  const { getAuth, onAuthStateChanged } = await authModule();
  listeners.add(cb);
  const off = onAuthStateChanged(getAuth(getApp()), (u) => {
    if (u) void touchActivity(u.uid);
    cb(plain(u));
  });
  return () => {
    listeners.delete(cb);
    off();
  };
}

const ACTIVE_KEY = "makeit:activeDay:";

/**
 * Marks the account as used today (users/{uid}.lastActiveAt), at most once a
 * day per device, so inactive accounts can be found later. It also says the
 * browser's country (data/country.ts): the first one, just after signing up,
 * stays with the account (users/{uid}.country).
 */
async function touchActivity(uid: string): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  try {
    if (localStorage.getItem(ACTIVE_KEY + uid) === today) return;
  } catch {
    /* no storage: touch anyway */
  }
  try {
    const country = await import("./country").then((m) => m.deviceCountry()).catch(() => null);
    await callAccountFn<{ action: "touch"; from: "web"; country: string | null }, { ok: true }>("manageAccount", { action: "touch", from: "web", country });
    localStorage.setItem(ACTIVE_KEY + uid, today);
  } catch {
    /* tried again next time */
  }
}

/** After a profile change (the name): Auth does not report those by itself. */
async function refreshUser(): Promise<void> {
  const { getAuth } = await authModule();
  const u = getAuth(getApp()).currentUser;
  if (!u) return;
  await u.reload();
  const p = plain(u);
  listeners.forEach((cb) => cb(p));
}

export class AuthError extends Error {
  constructor(
    public readonly code: "cancelled" | "invalid-email" | "wrong-password" | "user-not-found" | "email-in-use" | "weak-password" | "too-many" | "network" | "recent-login" | "unknown",
    message?: string,
  ) {
    super(message ?? code);
    this.name = "AuthError";
  }
}

function mapAuthError(err: unknown): AuthError {
  const code = String((err as { code?: string })?.code ?? "");
  switch (code) {
    case "auth/invalid-email":
      return new AuthError("invalid-email");
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return new AuthError("wrong-password");
    case "auth/user-not-found":
      return new AuthError("user-not-found");
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
    case "auth/user-cancelled":
      return new AuthError("cancelled");
    case "auth/email-already-in-use":
    case "auth/account-exists-with-different-credential":
    case "auth/credential-already-in-use":
    case "functions/already-exists":
      return new AuthError("email-in-use");
    case "auth/weak-password":
      return new AuthError("weak-password");
    case "auth/too-many-requests":
      return new AuthError("too-many");
    case "auth/network-request-failed":
    case "functions/unavailable":
    case "functions/deadline-exceeded":
      return new AuthError("network");
    case "auth/requires-recent-login":
    case "functions/failed-precondition":
      return new AuthError("recent-login");
    default:
      return new AuthError("unknown", code || String(err));
  }
}

export async function signIn(email: string, password: string): Promise<void> {
  const { getAuth, signInWithEmailAndPassword, setPersistence, browserLocalPersistence } = await authModule();
  const auth = getAuth(getApp());
  try {
    await setPersistence(auth, browserLocalPersistence);
    await signInWithEmailAndPassword(auth, email.trim(), password);
  } catch (err) {
    throw mapAuthError(err);
  }
}

/** Apple's sign-in, in a popup (Firebase's web flow: the Services ID set up in Firebase › Auth › Apple). */
async function appleProvider() {
  const { OAuthProvider } = await authModule();
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return provider;
}

/** Sign in with Apple: an account created with Apple on iOS (or one that also has Apple). */
export async function signInWithApple(): Promise<void> {
  const { getAuth, signInWithPopup, setPersistence, browserLocalPersistence } = await authModule();
  const auth = getAuth(getApp());
  try {
    await setPersistence(auth, browserLocalPersistence);
    await signInWithPopup(auth, await appleProvider());
  } catch (err) {
    throw mapAuthError(err);
  }
}

export async function signOut(): Promise<void> {
  const { getAuth, signOut: out } = await authModule();
  await out(getAuth(getApp()));
}

export async function resetPassword(email: string): Promise<void> {
  const { getAuth, sendPasswordResetEmail } = await authModule();
  try {
    await sendPasswordResetEmail(getAuth(getApp()), email.trim());
  } catch (err) {
    throw mapAuthError(err);
  }
}

/** Web accounts live only in Firebase Auth (the Unity app will read Firebase later). */
export async function register(email: string, password: string, name: string): Promise<void> {
  const { getAuth, createUserWithEmailAndPassword, updateProfile, setPersistence, browserLocalPersistence } = await authModule();
  const auth = getAuth(getApp());
  try {
    await setPersistence(auth, browserLocalPersistence);
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    if (name.trim()) await updateProfile(cred.user, { displayName: name.trim() });
  } catch (err) {
    throw mapAuthError(err);
  }
}

// ---------------------------------------------------------------------------
// Account (the Account page): name, password, deleting the account.
// ---------------------------------------------------------------------------

/** Signs in again with the password (Firebase asks for a recent sign-in before sensitive changes). */
async function reauthenticate(password: string) {
  const mod = await authModule();
  const u = mod.getAuth(getApp()).currentUser;
  if (!u?.email) throw new AuthError("user-not-found");
  try {
    await mod.reauthenticateWithCredential(u, mod.EmailAuthProvider.credential(u.email, password));
  } catch (err) {
    throw mapAuthError(err);
  }
  return { mod, user: u };
}

async function reauthenticateWithApple() {
  const mod = await authModule();
  const u = mod.getAuth(getApp()).currentUser;
  if (!u) throw new AuthError("user-not-found");
  try {
    await mod.reauthenticateWithPopup(u, await appleProvider());
  } catch (err) {
    throw mapAuthError(err);
  }
}

export async function changePassword(current: string, next: string): Promise<void> {
  const { mod, user } = await reauthenticate(current);
  try {
    await mod.updatePassword(user, next);
  } catch (err) {
    throw mapAuthError(err);
  }
}

async function callAccountFn<I, O>(name: string, data: I, timeout?: number): Promise<O> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const call = httpsCallable<I, O>(getFunctions(getApp(), FUNCTIONS_REGION), name, timeout ? { timeout } : undefined);
  try {
    return (await call(data)).data;
  } catch (err) {
    throw mapAuthError(err);
  }
}

/** The account's name (Auth displayName; users/{uid}.name too). */
export async function changeName(name: string): Promise<void> {
  await callAccountFn<{ action: "profile"; name: string }, { name: string }>("manageAccount", { action: "profile", name: name.trim() });
  await refreshUser();
}

/**
 * Deletes the account and everything it owns, for good (deleteAccount). The
 * password is asked again first (an account without one signs in with Apple
 * again instead); then the session ends.
 */
export async function deleteAccount(confirm: { password: string } | { apple: true }): Promise<void> {
  if ("apple" in confirm) await reauthenticateWithApple();
  else await reauthenticate(confirm.password);
  await callAccountFn<Record<string, never>, { ok: true }>("deleteAccount", {}, 540_000);
  try {
    await signOut();
  } catch {
    /* the user is gone already */
  }
}
